import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";

interface ZoneSidebarContextType {
  /** DOM element where zone sidebar content will be portaled into */
  portalContainer: HTMLDivElement | null;
  /** Setter for the portal container — used as a callback ref by ZoneSidebar */
  setPortalContainer: (el: HTMLDivElement | null) => void;
  /** Whether the sidebar is collapsed */
  collapsed: boolean;
  /** Toggle collapsed state */
  toggleCollapsed: () => void;
}

const ZoneSidebarContext = createContext<ZoneSidebarContextType | null>(null);

export function ZoneSidebarProvider({ children }: { children: ReactNode }) {
  const [portalContainer, setPortalContainer] =
    useState<HTMLDivElement | null>(null);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("zoneSidebarCollapsed") === "true",
  );

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      localStorage.setItem("zoneSidebarCollapsed", String(!prev));
      return !prev;
    });
  }, []);

  return (
    <ZoneSidebarContext.Provider
      value={{ portalContainer, setPortalContainer, collapsed, toggleCollapsed }}
    >
      {children}
    </ZoneSidebarContext.Provider>
  );
}

/** Hook for zone components to access the sidebar portal and collapsed state */
export function useZoneSidebar() {
  const ctx = useContext(ZoneSidebarContext);
  if (!ctx) {
    return {
      portalContainer: null as HTMLDivElement | null,
      collapsed: false,
      toggleCollapsed: () => {},
    };
  }
  return ctx;
}

export default ZoneSidebarContext;
